import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { patientPunchCardsQueryKey } from './usePatientPunchCards';
import { patientAppointmentsQueryKey } from './usePatientAppointments';

export interface ActivePunchCard {
  id: string;
  name: string;
  total_clips: number;
  used_clips: number;
  remaining_clips: number;
  expires_at: string | null;
  appointment_types: unknown[];
  group_types: unknown[];
  status: string;
}

export function usePatientActivePunchCards(patientId: string | null, clinicId: string | null) {
  return useQuery({
    queryKey: ['punch-cards', 'active', patientId, clinicId],
    enabled: Boolean(patientId) && Boolean(clinicId),
    queryFn: async (): Promise<ActivePunchCard[]> => {
      if (!patientId || !clinicId) return [];
      
      const { data, error } = await supabase.rpc('get_patient_active_punch_cards', {
        p_patient_id: patientId,
        p_clinic_id: clinicId,
      });
      
      if (error) throw error;
      return (data ?? []) as unknown as ActivePunchCard[];
    },
    staleTime: 10_000,
  });
}

export function usePayWithPunchCard() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: {
      punch_card_id: string;
      appointment_id: string;
      patient_id: string;
      clinic_id: string;
      clips_to_use?: number;
    }) => {
      const { data, error } = await supabase.rpc('use_punch_card_for_appointment', {
        p_punch_card_id: input.punch_card_id,
        p_appointment_id: input.appointment_id,
        p_clips_to_use: input.clips_to_use ?? 1,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: patientPunchCardsQueryKey(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: patientAppointmentsQueryKey(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: ['punch-cards', 'active', variables.patient_id, variables.clinic_id] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useRevertPunchCardUsage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: { usage_id: string; patient_id: string; clinic_id: string }) => {
      const { data, error } = await supabase.rpc('revert_punch_card_usage', {
        p_usage_id: input.usage_id,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: patientPunchCardsQueryKey(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: patientAppointmentsQueryKey(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
